import React, {
  Component,
  forwardRef,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TextFieldEditor from '../../components/TextFieldEditor';
import { viewActions } from '../../state/modules/view';
import _get from 'lodash/get';
import * as Addons from '../../components/react-data-grid-addons';
import * as Breakpoints from '../../utils/ResponsiveBreakpoints';
import styles from '../../styles/partials/_datagrid.css';
import getIn from '../../components/connectedForm/utils/getIn';
import Formatters from '../../components/grid-addons/formatters';
import * as UuIdUtils from '../../utils/UuIdGenerator';
import ExpandedRowRenderer from './ExpandedRowRenderer';
import WorkspaceContextMenu from '../Workspace/WorkspaceContextMenu';
import iconMixin from '../../styles/utils/iconMixin';
import styled from 'styled-components';
import EditableDataGridContext from '../Context/EditableDataGridContext';
import ModalContext from '../Context/ModalContext';
import { useDispatch, useSelector } from 'react-redux';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { AlertType, Direction, Maybe } from '../../graphql/schema/p4types';
import { IColumnStyles, ICommit, IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { Dispatch } from 'redux';
import TextFieldFormatter from '../grid-addons/formatters/TextFieldFormatter';
import ReactDataGrid, { IOnPressKeyOptions, IRowSelection } from '../react-data-grid/ReactDataGrid';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import CheckboxInputEditor from '../CheckboxInputEditor/CheckboxInputEditor';
import { useRefFunction } from '../../utils/hooks';
import { serviceActions } from '../../state/modules/service';
import onClickOutside from 'react-click-outside';
import { IFormatterProps } from '../../@types/workspaceTypes';

const { DraggableContainer } = Addons.DraggableHeader as any;
const ClickOutsideReactDataGrid = onClickOutside(ReactDataGrid);

const EMPTY = {};

const Icon = styled.div`
  ${iconMixin('48px')};
  color: #ffa000;
  padding: 10px 10px 0 0;
  float: left;
`;

//Incomplete
export type IRowUpdatedFunction<RowDataType = any> = (
  commit: ICommit<RowDataType>,
  dataid?: string,
  additional?: { connectedForm?: any }
) => void;

export type IOnCellSelectedFunction<RowDataType = any> = (
  dataid: string,
  selected: { rowIdx: number; idx: number },
  rowData: RowDataType
) => void;

const checkInitialSort = (rows: Array<any>, initialSortColumn?: string) => {
  if (!!initialSortColumn) {
    rows.sort((a, b) => {
      return getIn(a, initialSortColumn) > getIn(b, initialSortColumn) ? 1 : -1;
    });
  }
  return rows;
};

const getRowId = (row: any): number | null => {
  if (row._originalId !== null && row._originalId !== undefined) {
    return row._originalId as number;
  }
  if (row.id !== undefined) {
    return row.id as number;
  }
  return null;
};

const generateRowIdMap = (rows: Array<any>, { readOnly }: { readOnly?: boolean }) => {
  let id;
  const rowIdMap: IRowMap = {
    toIndex: {},
    toId: {},
  };
  rows.map((row, index) => {
    id = getRowId(row);
    if (id === null && readOnly) id = index;
    if (id === null) {
      throw new Error(`Cannot load grid, row ${index + 1} does not have an id`);
    }
    if (rowIdMap[id] !== undefined) {
      throw new Error(
        `Cannot load grid, row ${index + 1} has id ${id} which is already used by row ${rowIdMap[id] + 1}`
      );
    }
    rowIdMap.toIndex[id] = index;
    rowIdMap.toId[index] = id;
  });
  return rowIdMap;
};

export type IEditableDataGridProps<RowDataType = any> = {
  columns: Array<IEditableDataGridColumnDef>;
  enableSelectAll?: boolean;
  enableCellSelect?: boolean;
  enableCellSelectColumns?: Array<string>;
  enableCellAutoFocus?: boolean;
  onCellSelected?: IOnCellSelectedFunction;
  withRowUpdated?: IRowUpdatedFunction<RowDataType>;
  onRowUpdated?: IRowUpdatedFunction<RowDataType>;
  customChange?: (parameters: {
    commit: ICommit<RowDataType>;
    updateStore: Function;
    dataid: string;
    column?: { temporaryOnChange?: boolean };
  }) => void;
  enableContextMenu?: boolean;
  renderActionButtons?: (options: { rowData: any; rowIdx: number }) => Array<JSX.Element | ReactElement>;
  addBlankRow?: boolean;
  contentHeight?: Maybe<number>;
  initialSortColumn?: string;
  dataPath?: string;
  dataid?: string;
  listData?: Array<any>;
  readOnly?: boolean;
  queueRequestFor?: (rowIndex: number) => void; //Requests data based on a rowIndex.
  value?: any;
  mergeEntity?: string;
  mergeObject?: any;
  rowsCount?: number;
  entityType?: string;
  pageSize?: number;
  initialSortDir?: string;
  id?: string;
  className?: string;
  name?: string;
  expandableRows?: boolean;
  showSpinnerWhileLoading?: boolean;
  ignoreKeypresses?: boolean;
  message?: Array<any>;
  onRowClick?: (rowIdx: number, rowData: RowDataType, column: any) => void;
  onRowDoubleClick?: Function;
  onRowRightClick?: Function;
  deleteOption?: (rowIdx: number) => void;
  onGridSort?: (sortColumn: string, sortDirection: Direction) => void;
  enableRowSelect?: boolean;
  dispatch?: Dispatch;
  screenName?: string;
  connectedForm?: any;
  mergeEditableData?: boolean;
  onFocus?: Function;
  onColumnReorder?: Function;
  isLoading?: boolean;
  contentWidth?: number;

  /**
   * Note - Autowidth has indirect usage - This is used by the ConnectedField wrapper component to prevent it from calculating and providing a contentWidth (In createFieldProps)
   * If using EditableDataGrid directly, can just 'not provide' contentWidth.
   */
  autoWidth?: boolean;
  autoContentHeight?: boolean;
  rowHeight?: number;
  headerRowHeight?: number;
  maxHeight?: number;
  rowRenderer?: any;
  contextKey?: string;
  checkboxSide?: 'left' | 'right';
  onAllRowsSelected?: (all: boolean) => void;
  selectedAll?: boolean;
  selectedRowIds?: Array<string | number>;
  onRowSort?: (params: { rowSource: { idx: number }; rowTarget: { idx: number } }) => void;
  hideArchivedLines?: boolean;
  testId?: string;
  sortColumn?: string;
  sortDirection?: Direction;
  emptyRowsView?: ReactNode;
  rowKey?: string;
  gridType?: 'workspace';
  getTabOptions?: (commit: any) => { xOffset: number } | null;
  disableVirtualRows?: boolean;
  onRowSelect?: Function;
  actionEntities?: { [key: string]: Array<any> };
  miniGrid?: boolean;
  ignoreMutationIdCheck?: boolean; //Used indirectly (When props are passed through to other elements)
  dataStoreId?: string;
  temporaryOnFocus?: boolean | (() => boolean);
  temporaryOnChange?: boolean;
  initialized?: boolean; // may be required for temp saves
  initialAddingNewNote?: boolean;
  onPressKey?: {
    numpadAdd: (e: KeyboardEvent, options: IOnPressKeyOptions) => void;
  };
  format?: any; //Used by BankReconciliationForm
  handleGridSort?: Function;
  originalRowIdFromDisplay?: Function;
  rows?: Array<any>;
  moveColumn?: Function;
  findColumn?: Function;
  displayRowMap?: any;
  setColumns?: React.Dispatch<React.SetStateAction<Array<any>>>;
  isDragging?: boolean;
  ignoreContainerWidth?: boolean;

  /**
   * You must also set "disableVirtualRows" = true in props if using hideLinesCondition to hide rows
   * @param rowData
   */
  hideLinesCondition?: (rowData?: any) => boolean;
  handleColumnResize?: (fieldName: string, width: number) => void;
  selectCellFormatter?: (props: IFormatterProps<ISalesOrderLine>) => ReactNode;

  /**
   * When editing is enabled, cells will default to being editable: true.
   * This flag is to allow that behaviour to be changed (Typical usage is an editable Workspace so that extra columns aren't unexpectable editable).
   */
  defaultEditable?: boolean;
};

type IEditableDataGridState = {
  selectedAll: boolean;
  error?: any;
  errorInfo?: any;
  dragId?: any;
  dragPos?: any;
};

type IRowMap = { toIndex: { [id: number]: number }; toId: { [index: number]: number }; [id: number]: any };

export const EditableDataGridLoadingSpinner = () => (
  <div className="no-results" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    <Spinner size={SpinnerSize.large} />
  </div>
);

export const EditableDataGridEmptyComponent = () => (
  <div className="no-results">
    <Icon>error_outline</Icon>
    <h4>No details were found to display</h4>
    <p>Modify your filters or Add new items</p>
  </div>
);

class EditableDataGrid extends Component<IEditableDataGridProps, IEditableDataGridState> {
  private dataStoreId?: string;
  private validationMessage: Maybe<string>;
  //Requested rows is a trace of which rows have requested a fetchMore in a workspace.
  //It is reset when the grid mounts.
  //It will cause a row to query for new data even if it is already populated from the apollo cache.
  private requestedRows: Array<boolean>;
  private readonly gridRef: React.RefObject<any>;
  static displayName: string;
  static defaultProps: {
    enableRowSelect: boolean;
    pageSize: number;
    className: string;
    onGridSort: null;
    mergeEditableData: boolean;
    emptyRowsView: null;
    sortDirection: null;
    gridType: string;
    actionEntities: { [key: string]: any[] };
    headerRowHeight: number;
    id: string;
    showSpinnerWhileLoading: boolean;
    rowHeight: number;
    onRowDoubleClick: () => void;
    addBlankRow: boolean;
    contextKey: string;
    updateColumns: null;
    pageText: string;
    expandableRows: boolean;
    onRowClick: () => void;
    onRowRightClick: () => void;
    message: any[];
    ignoreKeypresses: boolean;
    onFocus: null;
    rowId: string;
    isLoading: boolean;
    queueRequestFor: () => void;
    initialSortDir: string;
    sortColumn: null;
    dataid: string;
    enableContextMenu: boolean;
    onCellSelected: null;
    testId: string;
    autoContentHeight: boolean;
    enableCellAutoFocus: boolean;
  };

  constructor(props: IEditableDataGridProps) {
    super(props);
    this.rowGetter = this.rowGetter.bind(this);
    this.onHeaderDrop = this.onHeaderDrop.bind(this);
    this.mergeRowGetter = this.mergeRowGetter.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this.onRowDoubleClick = this.onRowDoubleClick.bind(this);
    this.handleColumnResize = this.handleColumnResize.bind(this);
    this.handleCellSelected = this.handleCellSelected.bind(this);
    this.onRowSelect = this.onRowSelect.bind(this);
    this.onRowsSelected = this.onRowsSelected.bind(this);
    this.onRowsDeselected = this.onRowsDeselected.bind(this);

    this.dataStoreId = props.dataStoreId;
    this.requestedRows = [];

    // If grid supports local sorting all data must be loaded but updates will need mapping to the correct line
    // based on where it has been sorted to.  Generate map of ID to displayed row index
    this.validationMessage = null;

    this.state = {
      selectedAll: false,
    };

    this.gridRef = React.createRef();
  }

  componentDidMount() {
    this.requestedRows = []; //Reset requested list on re-mount.
  }

  // Merges the editable data with the main row data used to populate the grid
  // Created as new function to avoid continued checking of prop
  mergeRowGetter(rowIndex: number) {
    if (!this.props.rows) return {};
    if (!this.props.rows[rowIndex] || !this.requestedRows[rowIndex]) {
      this.props.queueRequestFor?.(rowIndex);
      this.requestedRows[rowIndex] = true;
      return {};
    }
    let row = this.props.rows[rowIndex];
    if (row.node) row = row.node;
    const { value, mergeEntity } = this.props;
    let inputData: Array<any>;
    if (Array.isArray(value)) {
      const mergePath = mergeEntity ? [mergeEntity, 'id'] : 'id';
      inputData = value.filter((item) => getIn(item, mergePath) === row.id);
      if (inputData.length === 0) inputData = [this.props.mergeObject];
    } else {
      inputData = [this.props.mergeObject];
    }
    //Changed to only copy over the keys noted in the mergeObject, to attempt to avoid merging id's which could break later merges.
    row = Object.assign({}, row);
    Object.keys(this.props.mergeObject).forEach((key) => {
      row[key] = inputData[0][key];
    });

    const rowObject = Object.assign({}, row);
    rowObject.get = (key: string, data = row) => {
      return _get(data, key);
    };
    return rowObject;
  }

  rowGetter(rowIndex: number) {
    if (!this.props.rows || (!this.props.rows[rowIndex] && rowIndex >= (this.props.rowsCount || 0))) return {};
    if (!this.props.rows[rowIndex] || !this.requestedRows[rowIndex]) {
      this.props.queueRequestFor?.(rowIndex);
      this.requestedRows[rowIndex] = true;
      return {};
    }
    let row = this.props.rows[rowIndex];
    if (row.node) row = row.node;
    const rowObject = Object.assign({}, row);
    rowObject.get = (key: string, data = row) => {
      return _get(data, key);
    };
    return rowObject;
  }

  onRowClick(rowIdx: number, rowData: any, column: any) {
    this.props.onRowClick?.(rowIdx, rowData, column);
  }

  onRowDoubleClick(event: any, idx: number, row: any) {
    this.props.onRowDoubleClick?.(event.target, idx, row);
  }

  handleColumnResize(index: number, width: number) {
    const actualIndex = this.props.enableRowSelect ? index - 1 : index;
    let newWidth;
    if (width > window.innerWidth / 2) {
      this.props.dispatch?.(
        serviceActions.genericAlert(
          'Column Resize',
          'Columns cannot be set wider than 50% of the window width.',
          AlertType.Warning
        )
      );
      newWidth = window.innerWidth / 2;
    } else if (width < 30) {
      newWidth = 30;
    } else {
      newWidth = width;
    }

    const columnKey = this.props.columns[actualIndex].key;

    if (this.props.handleColumnResize && columnKey) {
      this.props.handleColumnResize(columnKey as string, newWidth);
    }

    if (this.props.screenName) {
      this.props.dispatch?.(
        viewActions.resizeColumn(this.props.screenName, this.props.columns[actualIndex].key, newWidth)
      );
    }
  }

  handleCellSelected(selected: { rowIdx: number; idx: number }) {
    if (!this.props.enableCellSelect && !this.props.onCellSelected) return;
    const rowData = this.props.mergeEditableData
      ? this.mergeRowGetter(selected.rowIdx)
      : this.rowGetter(selected.rowIdx);
    if (this.props.onCellSelected) this.props.onCellSelected(this.props.dataid as string, selected, rowData);
    this.validationMessage = '';
    const col = this.props.columns[selected.idx];

    if (col) {
      let rowIdx = selected.rowIdx;
      let messages: Array<any> = [];
      let key = col.key;

      if (this.props.displayRowMap.current) {
        rowIdx = this.props.originalRowIdFromDisplay?.(rowIdx);
      }

      if (col.options && (col.options as any).entityId) {
        key =
          ((col.options as any).entityPath ? `${(col.options as any).entityPath}.` : '') +
          (col.options as any).entityId;
      }

      if ((key as string).endsWith('.id')) {
        key = (key as string).slice(0, -3);
      }

      if (this.props.dataPath) {
        key = `${this.props.dataPath.replace(/\[[^]]*]\./g, '')}.${key as string}`;
      }

      if (this.props.message && this.props.message[rowIdx]) {
        messages = this.props.message[rowIdx][key as string];
      }

      if (messages && messages.length > 0) {
        messages.forEach((message) => {
          this.validationMessage += message.message;
        });
      }

      if (this.props.onFocus && (this.props.name || this.props.dataid)) {
        const name = this.props.name || this.props.dataid;
        const fieldName = `${name}.${rowIdx}.${col.key as string}`;
        this.props.onFocus(fieldName, name, rowIdx, col.key);
      }
    }
  }

  shouldComponentUpdate(nextProps: IEditableDataGridProps, nextState: IEditableDataGridState) {
    return !nextProps.isDragging;
  }

  onHeaderDrop = (source: any, target: any) => {
    const columnSourceIndex = this.props.columns.findIndex((i) => i.key === source);
    const columnTargetIndex = this.props.columns.findIndex((i) => i.key === target);

    const columnsCopy = [...this.props.columns];

    columnsCopy.splice(columnTargetIndex, 0, columnsCopy.splice(columnSourceIndex, 1)[0]);

    this.props.setColumns?.(columnsCopy);

    if (this.props.onColumnReorder) {
      this.props.onColumnReorder(columnsCopy);
    }
  };

  // eslint-disable-next-line complexity
  render() {
    let EmptyGridView: ReactNode | null = null;
    if (!this.props.isLoading) {
      EmptyGridView = this.props.emptyRowsView ? this.props.emptyRowsView : EditableDataGridEmptyComponent;
    } else if (this.props.showSpinnerWhileLoading) {
      EmptyGridView = EditableDataGridLoadingSpinner;
    }

    const {
      enableSelectAll,
      enableCellSelect,
      enableCellSelectColumns,
      contentWidth,
      contentHeight,
      autoContentHeight,
      rowHeight,
      headerRowHeight,
      maxHeight,
    } = this.props;
    let rowsCount = this.props.rowsCount ?? 0;
    if (this.props.dataPath || (!rowsCount && this.props.rows && this.props.rows.length > 0)) {
      rowsCount = this.props.rows?.length ?? 0;
      if (this.props.addBlankRow && !this.props.readOnly) {
        rowsCount++;
      }
    }

    if (this.props.columns.length === 0) return null;
    let minHeight: Maybe<number> | undefined = contentHeight;

    if (autoContentHeight && rowHeight && rowsCount && headerRowHeight) {
      const horzScroll = 20;
      minHeight = rowHeight * rowsCount + headerRowHeight + horzScroll;

      if (maxHeight && minHeight > maxHeight) {
        minHeight = maxHeight;
      }

      if (minHeight === 0) {
        minHeight = undefined;
      }
    }

    //if (!this.props.listData && !this.props.addBlankRow) return (<div></div>);

    const validationMessage = this.validationMessage ? (
      <div className={styles.validation}>
        <div>{this.validationMessage}</div>
      </div>
    ) : null;

    let expansionProp = {};
    if (this.props.expandableRows || this.props.rowRenderer) {
      expansionProp = {
        rowRenderer: this.props.rowRenderer || ExpandedRowRenderer,
      };
    }

    const actionMenu = this.props.enableContextMenu ? (
      <WorkspaceContextMenu
        key={'ContextMenu'}
        renderActionButtons={this.props.renderActionButtons}
        rowGetter={this.rowGetter}
        dispatch={this.props.dispatch}
        identifier={'gridActionMenu'}
        contextKey={this.props.contextKey}
        dataid={this.props.dataid}
        deleteOption={this.props.deleteOption}
      />
    ) : null;

    const classname = this.props.className ? `${styles.container} ${this.props.className}` : styles.container;

    const rowSelection: IRowSelection = {
      showCheckbox: this.props.enableRowSelect,
      checkboxSide: this.props.checkboxSide || 'left', //'left' or 'right' only valid options.
      enableShiftSelect: true,
      onRowsSelected: this.onRowsSelected,
      onRowsDeselected: this.onRowsDeselected,
      onAllRowsSelected: this.props.onAllRowsSelected,
      selectBy: {
        all: this.props.selectedAll,
        indexes: this.props.selectedRowIds || [],
      },
    };

    return (
      <EditableDataGridContext.Provider
        value={{
          ownerGrid: this.props.name || this.props.id,
          onRowSort: this.props.onRowSort,
          hideArchivedLines: this.props.hideArchivedLines,
          contentWidth: this.props.contentWidth,
          hideLinesCondition: this.props.hideLinesCondition,
          ignoreKeypresses: this.props.ignoreKeypresses,
        }}
      >
        <div className={classname} data-testid={`grid-container-view-${this.props.screenName}`}>
          {validationMessage}
          <DraggableContainer onHeaderDrop={this.onHeaderDrop}>
            <ClickOutsideReactDataGrid
              ref={this.gridRef}
              columns={this.props.columns}
              enableSelectAll={enableSelectAll}
              enableCellSelect={enableCellSelect}
              enableCellSelectColumns={enableCellSelectColumns}
              onCellSelected={this.handleCellSelected}
              onRowUpdated={this.props.onRowUpdated}
              onRowClick={this.onRowClick}
              onRowDoubleClick={this.props.onRowDoubleClick}
              onRowRightClick={this.props.onRowRightClick}
              rowGetter={this.props.mergeEditableData ? this.mergeRowGetter : this.rowGetter}
              rowHeight={this.props.rowHeight}
              {...expansionProp}
              headerRowHeight={this.props.headerRowHeight}
              rowsCount={rowsCount}
              onGridSort={this.props.handleGridSort!}
              onColumnResize={this.handleColumnResize}
              sortColumn={this.props.sortColumn}
              sortDirection={this.props.sortDirection}
              moveColumn={this.props.moveColumn}
              findColumn={this.props.findColumn}
              emptyRowsView={EmptyGridView}
              minWidth={contentWidth}
              minHeight={minHeight}
              rowKey={this.props.rowKey}
              enableCellAutoFocus={this.props.enableCellAutoFocus}
              rowSelection={rowSelection}
              dataid={this.props.dataid}
              testId={this.props.testId}
              contextMenu={actionMenu}
              contextKey={this.props.contextKey}
              gridType={this.props.gridType}
              getTabOptions={this.props.getTabOptions}
              ignoreKeypresses={this.props.ignoreKeypresses}
              disableVirtualRows={this.props.disableVirtualRows}
              onPressKey={this.props.onPressKey}
              ignoreContainerWidth={this.props.ignoreContainerWidth}
            />
          </DraggableContainer>
        </div>
      </EditableDataGridContext.Provider>
    );
  }

  onRowsSelected(rows: Array<any>) {
    const newSelectedRowIds = this.props.selectedRowIds?.concat(rows.map((r) => r.row.uuid || r.rowIdx));
    this.onRowSelect(newSelectedRowIds ?? []);
  }

  onRowsDeselected(rows: Array<any>) {
    const rowIndexes = rows.map((r) => r.row.uuid || r.rowIdx);
    const newSelectedRowIds = this.props.selectedRowIds?.filter((i) => rowIndexes.indexOf(i) === -1);
    this.onRowSelect(newSelectedRowIds ?? []);
  }

  onRowSelect(newSelectedRowIds: Array<string | number>) {
    if (!this.props.onRowSelect) return;

    const selectedDataRows = newSelectedRowIds
      .map((selected) => {
        let existingEntity = null;
        if (this.props.actionEntities?.[this.props.screenName as string]) {
          existingEntity = this.props.actionEntities[this.props.screenName as string].find(
            (value: any) => value && value.uuid === selected
          );
        }
        if (existingEntity) return existingEntity;

        const row = this.props.rows?.find((_row, rowIdx) => {
          if (_row && _row.uuid) return _row.uuid === selected;
          return _row?.node?.uuid ? _row.node.uuid === selected : rowIdx === selected;
        });
        if (!row) {
          console.error('Selected row could not be found & will be filtered out of the selection list', { row });
        }
        return row && row.node ? row.node : row;
      })
      .filter((row) => !!row);
    this.props.onRowSelect(newSelectedRowIds, selectedDataRows);
  }
}

EditableDataGrid.defaultProps = {
  isLoading: false,
  pageText: 'Items',
  pageSize: 20,
  rowHeight: 36,
  headerRowHeight: 36,
  id: 'ws-grid',
  dataid: '',
  initialSortDir: 'ASC',
  className: '',
  onGridSort: null,
  sortColumn: null,
  sortDirection: null,
  queueRequestFor: () => {},
  onRowClick: () => {},
  onRowDoubleClick: () => {},
  onRowRightClick: () => {},
  updateColumns: null,
  addBlankRow: false,
  rowId: '',
  enableRowSelect: false,
  enableContextMenu: true,
  onFocus: null,
  emptyRowsView: null,
  message: [],
  enableCellAutoFocus: true,
  autoContentHeight: false,
  onCellSelected: null,
  testId: 'datagrid',
  mergeEditableData: false,
  expandableRows: false,
  contextKey: 'default', //Used to link context menu trigger and menu options.
  gridType: 'unknown',
  actionEntities: {},
  showSpinnerWhileLoading: false,
  ignoreKeypresses: false,
};

EditableDataGrid.displayName = 'EditableDataGrid';

//@deprecated - Do not provide formatters by text string, just pass the formatter directly as the property.
//  i.e. "formatter: 'text'" should be written as "formatter: 'TextFieldFormatter'".
//  It makes it much easier to jump around the code base than using this in-direct approach.
const getFormatter = (type: IEditableDataGridColumnDef['formatter'], key: string) => {
  let formatter: ReactNode;
  switch (type) {
    case 'text':
      formatter = Formatters.TextFieldFormatter;
      break;
    case 'numeric':
      formatter = Formatters.NumericFormatter;
      break;
    case 'link':
      formatter = Formatters.LinkFormatter;
      break;
    case 'deleted':
      formatter = Formatters.DeletedFormatter;
      break;
    case 'expand':
      formatter = Formatters.ExpandFormatter;
      break;
    case 'selected':
      formatter = Formatters.SelectedFormatter;
      break;
    case 'boolean':
      formatter = Formatters.BooleanFormatter;
      break;
    case 'image':
      formatter = Formatters.ImageFormatter;
      break;
    case 'date':
      formatter = Formatters.DateFormatter;
      break;
    case 'dateTime':
      formatter = Formatters.DateTimeFormatter;
      break;
    case 'duration':
      formatter = Formatters.DurationFormatter;
      break;
    case 'dropdown':
      formatter = Formatters.DropdownFormatter;
      break;
    case 'enum':
      formatter = Formatters.EnumFormatter;
      break;
    case 'freeTypeDropdown':
      formatter = Formatters.FreeTypeDropdownFormatter;
      break;
    case 'AttributeFormatter':
      formatter = Formatters.AttributeFormatter;
      if (key.startsWith('dimensionSet')) formatter = Formatters.DimensionFormatter;
      if (key.startsWith('sellingPrices')) formatter = Formatters.SellingPriceFormatter;
      break;
    case 'MultiFormatter':
      formatter = Formatters.MultiFormatter;
      break;
    case 'DimensionFormatter':
      formatter = Formatters.DimensionFormatter;
      break;
    case 'SellingPriceFormatter':
      formatter = Formatters.SellingPriceFormatter;
      break;
    default:
      formatter = type;
      break;
  }
  return formatter;
};

const useGetRowMetaData = ({
  dataid,
  dataPath,
  message,
  originalRowIdFromDisplay,
}: {
  dataid?: string;
  dataPath?: string;
  message: any;
  originalRowIdFromDisplay: (id: number) => number;
}) => {
  const getCellValidationMessage = useCallback(
    (column, rowIdx) => {
      let validationMessage = null;
      let key = column.key;
      if (column.options && column.options.entityId) {
        key = (column.options.entityPath ? `${column.options.entityPath}.` : '') + column.options.entityId;
      }
      if (key.endsWith('.id')) {
        key = key.slice(0, -3);
      }
      if (dataPath) {
        key = `${dataPath.replace(/\[[^]]*]\./g, '')}.${key}`;
      }
      const dispRowIdx = originalRowIdFromDisplay(rowIdx);
      if (dataid && message && message[dispRowIdx] && message[dispRowIdx][key]) {
        message[dispRowIdx][key]?.forEach((message: any) => {
          validationMessage = message.message;
        });
      }
      return validationMessage;
    },
    [dataPath, dataid, message, originalRowIdFromDisplay]
  );

  return useCallback(
    (metaDataGetter) => {
      if (metaDataGetter) {
        return (rowData: any, column: any, rowIdx: number) => {
          const metaData = {
            message: getCellValidationMessage(column, rowIdx),
            cellIndex: `${dataid}-cell-${rowIdx}-${column.id}`,
          };

          return Object.assign(metaData, metaDataGetter(rowData, column, rowIdx));
        };
      }
      return (rowData: any, column: any, rowIdx: number) => {
        let message = getCellValidationMessage(column, rowIdx);

        const metaData = getIn(rowData, 'entityMetaData.metaData');
        if (metaData) {
          const validation = metaData.find((value: any) => value.key === column.key);
          const data = validation && validation.data;
          if (data) {
            message = data.join(', ');
          }
        }

        return {
          options: column.options,
          message: message,
          rowData: rowData,
          cellIndex: `${dataid}-cell-${rowIdx}-${column.id}`,
        };
      };
    },
    [dataid, getCellValidationMessage]
  );
};

const useParseColumns = ({
  originalRowIdFromDisplay,
  deleteOption,
}: {
  originalRowIdFromDisplay: (idx: number) => number;
  deleteOption?: (rowIdx: number) => void;
}) => {
  //Convenience function for adding a delete column to a nested grid. (A right-click delete is also added).
  const addDeleteColumn = useCallback(
    (columns) => {
      return [
        ...columns,
        {
          name: '',
          key: '',
          editable: true,
          resizable: true,
          formatter: Formatters.ContextOptionsFormatter,
          options: [
            {
              label: 'Delete',
              onClick: (_: unknown, _2: unknown, rowIdx: number) => {
                //Just need the rowIdx, first 2 params are irrelevant.
                deleteOption?.(rowIdx);
              },
            },
          ],
          cellClass: 'centerAlign',
          width: 75,
        },
      ];
    },
    [deleteOption]
  );

  // Call callback that requires rowIdx which needs mapping if table is sorted
  const mappedCallback = useCallback(
    (callback, idx) => {
      return callback(originalRowIdFromDisplay(idx));
    },
    [originalRowIdFromDisplay]
  );

  const sortColumns = useCallback((cols) => {
    const baseIdx = 100;
    const updatedColumns = cols.map((column: any) => {
      const col = Object.assign({}, column);
      col.sortIdx = baseIdx + col.position;
      if (col.key === 'id') col.sortIdx = 200;
      return col;
    });
    updatedColumns.sort((a: any, b: any) => a.sortIdx - b.sortIdx);

    return updatedColumns;
  }, []);

  return useCallback(
    ({
      columns,
      deleteOption,
      readOnly,
      onGridSort,
    }: {
      columns: Array<any>;
      deleteOption?: (rowIdx: number) => void;
      readOnly?: boolean;
      onGridSort?: (sortColumn: string, sortDirection: Direction) => void;
    }) => {
      if (columns.length === 0) return columns;

      let parsedColumns = columns;
      if (deleteOption) {
        parsedColumns = addDeleteColumn(parsedColumns);
      }

      if (!readOnly && onGridSort) {
        return sortColumns(parsedColumns);
      }

      parsedColumns = parsedColumns.map((column) => {
        if (!readOnly && typeof column.key !== 'function') {
          return column;
        }

        // Apply readOnly property to columns and perform any row mapping required for local sorting
        const col = Object.assign({}, column);
        if (readOnly) {
          col.editable = false;
        }
        if (typeof column.key === 'function') {
          col.key = mappedCallback.bind(null, column.key);
        }
        return col;
      });
      return sortColumns(parsedColumns);
    },
    [addDeleteColumn, mappedCallback, sortColumns]
  );
};

//Wrapper so we can access the store & context for altering the content width of the grid when nested in a modal.
const WrappedEditableDataGrid = forwardRef(
  (
    {
      contentWidth,
      ...props
    }: Omit<
      IEditableDataGridProps,
      | 'displayRowMap'
      | 'findColumn'
      | 'handleGridSort'
      | 'moveColumn'
      | 'originalRowIdFromDisplay'
      | 'rows'
      | 'setColumns'
    > & { contentWidth?: number },
    ref: Ref<any>
  ) => {
    const app = useSelector((store: any) => store.app);
    const modalContext = useContext(ModalContext);
    const connectedForm = useContext(ConnectedFormContext);
    const dispatch = useDispatch();

    const [rows, setRows] = useState<Array<any>>([]);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragId, setDragId] = useState<number | undefined>();
    const [dragPos, setDragPos] = useState<number | undefined>();

    const originalRowMap = useRef<Maybe<IRowMap>>(null);
    const displayRowMap = useRef<Maybe<IRowMap>>(null);

    const [columns, setColumns] = useState<Array<any>>([]);

    const { columns: baseColumns, deleteOption, readOnly, onGridSort } = props;

    // Get row index number for the blank row - this will be the row at the bottom of the table but could be either
    // a new row or the last row (which would have no id)
    const getBlankRowIndex = useRefFunction(() => {
      const index: number = rows!.length - 1;
      if (rows![index] && !rows![index].id) {
        return index;
      }
      return index + 1;
    });

    // Translate row index number from sorted data in grid into original row index (if local sorting in use)
    const originalRowIdFromDisplay = useCallback(
      (displayRowId) => {
        if (displayRowMap.current) {
          const id = displayRowMap.current.toId[displayRowId];
          if (id === undefined) {
            return getBlankRowIndex();
          }
          return originalRowMap.current?.toIndex[id];
        }

        return displayRowId;
      },
      [getBlankRowIndex]
    );

    const parseColumns = useParseColumns({ originalRowIdFromDisplay, deleteOption: props.deleteOption });

    const getRowMetaData = useGetRowMetaData({
      dataid: props.dataid,
      dataPath: props.dataPath,
      message: props.message,
      originalRowIdFromDisplay: originalRowIdFromDisplay,
    });

    const findColumn = (id: number) => {
      const column = columns[id];
      return {
        column,
        index: columns.indexOf(column),
      };
    };

    // eslint-disable-next-line complexity
    const getColumnProps = useCallback(
      (column: IEditableDataGridColumnDef) => {
        const resizable = {}.hasOwnProperty.call(column, 'resizable') ? column.resizable : true;
        const visible = {}.hasOwnProperty.call(column, 'visible') ? column.visible : true;
        const align = {}.hasOwnProperty.call(column, 'align') ? column.align : 'left';
        const cellClass = {}.hasOwnProperty.call(column, 'cellClass') ? column.cellClass : undefined;
        const widthFactor = column.widthFactor ?? undefined;
        let draggable, editable, sortable, styles, tooltip;
        if (!props.onColumnReorder) {
          draggable = false;
        } else {
          draggable = {}.hasOwnProperty.call(column, 'draggable') ? column.draggable : true;
        }
        if (!props.onGridSort || (column.key as string).toLowerCase().includes('attributevalue')) {
          sortable = false;
        } else if (column.isTransient) {
          sortable = false;
          styles = { headerCell: { backgroundColor: '#d0edff' } };
          tooltip = 'Dynamic column - Sorting/filtering is unavailable due to dynamic generation of value';
        } else {
          sortable = {}.hasOwnProperty.call(column, 'sortable') ? column.sortable : true;
        }
        if (props.readOnly) {
          editable = false;
        } else {
          const defaultEditable = props.defaultEditable ?? true;
          editable = {}.hasOwnProperty.call(column, 'editable') ? column.editable : defaultEditable;
        }

        const alignHeader =
          (column.formatter === 'numeric' ||
            (column.formatter &&
              (column.formatter as any).defaultProps &&
              (column.formatter as any).defaultProps.align === 'right')) &&
          props.className === 'entryGrid'
            ? 'right'
            : 'left';

        const columnStyles: IColumnStyles = {
          ...styles,
          ...column.styles,
          header: {
            textAlign: alignHeader,
            ...column.styles?.header,
          },
        };

        const { name, key, headerRenderer, cellFormatter } = column;

        const colProps: IEditableDataGridColumnDef = {
          name: name,
          key: key,
          align: align,
          resizable: resizable,
          sortable: sortable,
          visible: visible,
          draggable: draggable,
          cellClass: cellClass,
          editable: false,
          dispatch: dispatch,
          widthFactor,
          tooltip: column.tooltip || tooltip,
          styles: columnStyles,
          headerRenderer,
          cellFormatter,
        };
        colProps.getRowMetaData = getRowMetaData(column.getRowMetaData);
        if (editable) {
          colProps.editable = editable;
          colProps.cellEditableCheck = column.cellEditableCheck;
          colProps.getDefaultValue = column.getDefaultValue;
        }
        const AnyTextFieldEditor = TextFieldEditor as any;
        colProps.editor = <AnyTextFieldEditor key={`E_${(colProps.key as string) || 'MISSING_KEY'}`} />;
        colProps.formatter = <TextFieldFormatter key={`F_${(colProps.key as string) || 'MISSING_KEY'}`} />;
        if (column.editor) colProps.editor = column.editor;
        colProps.options = column.options ? column.options : EMPTY;
        if (column.editor === 'CheckboxInput') {
          colProps.editor = CheckboxInputEditor;
        }
        colProps.getDefaultValue = column.getDefaultValue;
        colProps.getRowMetaData = getRowMetaData(column.getRowMetaData);
        if (!column.formatter) {
          if (column.type === 'LocalDate' || column.type === 'LocalDateTime') {
            column.formatter = column.type === 'LocalDate' ? 'date' : 'dateTime';
          }
          if (column.type === 'Boolean') {
            column.formatter = 'boolean';
          }
          if (column.type === 'BigDecimal') {
            column.formatter = 'numeric';
            if (
              (column.key as string).toLowerCase().includes('price') ||
              (column.key as string).toLowerCase().startsWith('base')
            ) {
              column.options = { format: { decimals: 2 } };
            }
          }
        }
        if (column.formatter) {
          colProps.formatterType = column.formatter;
          colProps.formatter = getFormatter(column.formatter, column.key as string);
          colProps.options = column.options ? column.options : {};
        }
        colProps.width = column.width ? column.width : 0;
        if (window.innerWidth <= Breakpoints.TABLET_BREAKPOINT || (props.miniGrid && colProps.key !== 'id')) {
          colProps.width = 0;
        }
        colProps.tabSkip = column.tabSkip;
        colProps.cellTooltip = column.cellTooltip;
        return colProps;
      },
      [
        dispatch,
        getRowMetaData,
        props.className,
        props.miniGrid,
        props.onColumnReorder,
        props.onGridSort,
        props.readOnly,
      ]
    );

    const modalWidth = modalContext.style?.content?.width;
    let potentialGridWidth;
    if (modalWidth && contentWidth) {
      const numericPart = parseInt(modalWidth.replace(/(^\d+)(.+$)/i, '$1'), 10);

      if (modalWidth.match(/\d+%/g)) {
        //Percentile - Reduce the content width accordingly
        // (With an adjustment for the menu because contentWidth will include it if it's open and is not relevant for modals).
        potentialGridWidth = (((contentWidth || 0) + (app?.appMenuHidden ? 0 : 203)) * numericPart) / 100;
      } else {
        //Fixed - Use the smallest of the 2 (The provided content width or the modal's width).
        potentialGridWidth = numericPart < (contentWidth || 0) ? numericPart : contentWidth;
      }
    }

    const getTableData = (nextProps: IEditableDataGridProps) => {
      const rowList: Array<any> = [];
      for (const listItem of nextProps.listData!) {
        if (+listItem.pricingAttributeIndex === +connectedForm.registeredFields![nextProps.dataid!].selectedRow) {
          rowList.push(listItem);
        }
      }
      return rowList;
    };

    const handleGridSort = (sortColumn: string, sortDirection: Direction) => {
      if (props.onGridSort) {
        return props.onGridSort(sortColumn, sortDirection);
      }

      let comparator: Parameters<typeof rows['sort']>[0];

      if ((sortDirection as string) === 'NONE') {
        comparator = (a: any, b: any) => {
          return originalRowMap.current?.toIndex[getRowId(a)!]! < originalRowMap.current?.toIndex[getRowId(b)!]!
            ? -1
            : 1;
        };
      } else {
        if (!originalRowMap.current) {
          originalRowMap.current = generateRowIdMap(rows, { readOnly: props.readOnly });
        }

        const sortColProps = sortColumn.split('.');
        const numProps = sortColProps.length;
        comparator = (a: any, b: any) => {
          let i = 0;
          let x = a;
          let y = b;
          while (i < numProps && x !== null && y !== null) {
            x = x[sortColProps[i]];
            y = y[sortColProps[i]];
            i++;
          }
          if (sortDirection === 'ASC') {
            if (x === null) {
              return 1;
            }
            return x > y ? 1 : -1;
          } else if (sortDirection === 'DESC') {
            if (y === null) {
              return -1;
            }
            return x < y ? 1 : -1;
          }
          return 0;
        };
      }

      const sortedRows = [...rows];
      sortedRows.sort(comparator);
      displayRowMap.current = generateRowIdMap(sortedRows, { readOnly: props.readOnly });
      setRows(sortedRows);
    };

    /**
     * Copy listData into local rows state - Locally sort if no 'onGridSort' function is provided by the parent (For example, Workspaces provide this for remote sorting).
     */
    useEffect(() => {
      if (props.listData !== undefined) {
        let updatedRows: Array<any>;

        //Note - Expect/believe 'props.dataPath' to be deprecated/not-in-use. Should expect to go into the else and clone listData into updatedRows here.
        if (props.dataPath) {
          updatedRows = [];
          if (connectedForm.registeredFields && connectedForm.registeredFields[props.dataid!].selectedRow) {
            if (props.dataPath === props.dataid) {
              updatedRows = getTableData(props);
            } else {
              updatedRows = getIn(props.listData, [
                connectedForm.registeredFields[props.dataid!].selectedRow,
                props.dataPath,
              ]);
            }
          }
        } else {
          updatedRows = props.listData.slice();
        }

        if (!props.onGridSort) {
          checkInitialSort(updatedRows, props.initialSortColumn);

          // If grid supports local sorting and is sorted all data must be loaded but updates will need mapping to the
          // correct line based on where it has been sorted to
          if (displayRowMap.current) {
            let allowAdd: boolean;
            let id: number | null;
            let position: any;
            allowAdd = true; // When adding a row it will get id allocated from services, allow 1 row to be added
            const orderedRows: Array<any> = [];
            orderedRows.length = rows.length > 1 ? rows.length - 1 : 0;
            rows.forEach((row, index) => {
              id = getRowId(row);
              if (id === null) {
                if (allowAdd) {
                  // Null id will be present for a row being added before it is returned from services with an actual id
                  allowAdd = false;
                  orderedRows[getBlankRowIndex()] = row;
                  return;
                }
                throw new Error(`Cannot refresh grid, row ${index + 1} does not have an id`);
              }
              position = displayRowMap.current?.toIndex[id];

              // If row id not in map it must be a newly allocated id from the services due to being sent a blank row so
              // add this to the end of the map but only allow 1 row to be added in each pass
              if (position === undefined) {
                if (!allowAdd) {
                  return;
                }
                position = rows.length - 1;
                displayRowMap.current!.toIndex[id] = position;
                displayRowMap.current!.toId[position] = id;
                originalRowMap.current!.toIndex[id] = position;
                originalRowMap.current!.toId[position] = id;
                allowAdd = false;
              }
              orderedRows[position] = row;
            });
            updatedRows = orderedRows;
          }
        }

        setRows(updatedRows);
        setIsDragging(false);
      } else {
        setRows(checkInitialSort(props.listData ?? [], props.initialSortColumn));
      }

      //Various suspicious dependencies being hidden here - Cannot trust them to be appropriately memoised.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.listData, props.initialSortColumn, getBlankRowIndex]);

    useEffect(() => {
      setColumns(
        parseColumns({
          columns: baseColumns,
          deleteOption,
          readOnly,
          onGridSort,
        })
      );
      setIsDragging(false);

      //'deleteOption' and 'onGridSort' cannot be trusted to be appropriately memoised at this time.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseColumns, parseColumns, readOnly]);

    const createdColumns = useMemo(() => {
      const tableCols: Array<any> = [];
      let _idx = 0;
      columns.forEach((column) => {
        const colProps = getColumnProps(column);
        if (colProps.visible) {
          colProps.id = _idx;
          tableCols.push(colProps);
          _idx++;
        }
      });

      return tableCols;
    }, [columns, getColumnProps]);

    const moveColumn = (id: number, atIndex: number, dropped: any) => {
      if (dropped) {
        const { column, index } = findColumn(dragId!);

        const updatedColumns = { ...columns };
        updatedColumns.splice(index, 1);
        updatedColumns.splice(dragPos!, 0, column);

        setColumns(updatedColumns);
        setIsDragging(false);

        dispatch?.(viewActions.moveColumn(props.screenName, column?.key, dragPos));
      } else {
        setIsDragging(true);
        setDragId(id);
        setDragPos(atIndex);
      }
    };

    const { withRowUpdated, onRowUpdated, dataid } = props;
    const handleRowUpdated = useCallback(
      (commit) => {
        // Translate row index number from sorted data in grid into original row index (if local sorting in use)
        commit.rowIdx = originalRowIdFromDisplay?.(commit.rowIdx);

        // Get original and new values
        commit.original = rows[commit.rowIdx];

        if (withRowUpdated) {
          withRowUpdated(commit, dataid, { connectedForm });
        }

        if (!commit.original?.uuid) {
          if (!commit.cellKeys) {
            commit.cellKeys = [commit.cellKey];
          }
          commit.cellKeys.push('uuid');
          commit.updated.uuid = UuIdUtils.uuIdGenerator();
        }

        if (onRowUpdated) {
          onRowUpdated(commit, dataid, { connectedForm });
        }
      },
      [connectedForm, originalRowIdFromDisplay, withRowUpdated, onRowUpdated, dataid, rows]
    );

    return (
      <EditableDataGrid
        ref={ref}
        contentWidth={potentialGridWidth}
        connectedForm={connectedForm}
        {...props}
        handleGridSort={handleGridSort}
        originalRowIdFromDisplay={originalRowIdFromDisplay}
        columns={createdColumns}
        rows={rows}
        moveColumn={moveColumn}
        findColumn={findColumn}
        displayRowMap={displayRowMap}
        setColumns={setColumns}
        isDragging={isDragging}
        onRowUpdated={handleRowUpdated}
      />
    );
  }
);

//Hack to resolve other code that accesses elements by their displayName.
//Should really find a different way to implement those pieces of code
(WrappedEditableDataGrid as any).displayName = 'EditableDataGrid';

export default WrappedEditableDataGrid;
